import { FeatureFlagIds } from "../../../../utils/featureFlagIds";
import React, { useState, useEffect, ReactNode } from 'react';
import { GridProps } from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../../store/rootReducer';
import {
  canUnderwrite,
  applicationIsPastDesking,
  updateApplicationCustomFields,
  getApplicant
} from '../../../../utils/appUtils';
import ApplicationDetailsCard from '../../../ApplicationDetailsCard';
import { IConfiguration } from '../../../../Data/ConfigurationData';
import { updateEditingApp } from '../../../../store/application/actions';
import { ApplicationDispatch } from '../../../../store/application/types';
import { TabName } from '../OrderEditorTabs';
import { OverviewATC } from '../OverviewATC';
import { Constants, OverviewLayouts, permissions } from '../../../../utils/constants';
import { hasExistingCustomerInformation, userHasPermission } from '../../../../utils/utils';
import { useOutstandingApplicationCustomerStipulations } from '../../../../Data/StipulationData';
import { VehicleInfoCard } from "../VehicleCard/VehicleInfoCard";
import { StatusWorkflowComponent } from '../StatusWorkflowComponent';
import ChatIcon from '@mui/icons-material/Chat';
import { ApplicationChatComponent } from '../../../ApplicationChatComponent';
import { InvalidStipulationsCard } from '../../../InvalidStipulationsCard/InvalidStipulationsCard';
import { NotesCard } from '../../../NotesCard';
import { ExistingCustomerInformation } from '../ExistingCustomerInformation';
import {add, ApplicationQuoteType, usesFinanceQuote, divide, hasValue, multiply, PermissionSet} from '@amsanalytics/react-shared-library';
import { AmsApplicationInfoCard } from "../../../AmsApplicationInfoCard/AmsApplicationInfoCard";
import { useBusinessRulesEngine } from "../../../../Data/BusinessRulesData";
import {
  DecisionBoxIndirectDealerView
} from "../DecisionBoxIndirectDealerView";
import DecisionBoxIndirectUnderwriterView from "../../../DecisionBoxIndirectUnderwriterView";
import { IndirectApplicationHistoryCard } from "../IndirectApplicationHistoryCard";
import { CreditQualityAndRiskMetricsCard } from "../CreditQualityAndRiskMetricsCard";
import { IndirectApplicationDecisionInfoCard } from "../IndirectApplicationDecisionInfoCard";
import { CustomerCreditBreakdownCard } from "../CustomerCreditBreakdownCard";
import { IApplication } from "../../../../Interfaces/Application/IApplication";
import produce from "immer";
import { IEmployee } from "../../../../Interfaces/Admin/IEmployee";
import useMediaQuery from '@mui/material/useMediaQuery';
import { TileLayout, TileLayoutItem, TileLayoutRepositionEvent, TileStrictPosition } from '@progress/kendo-react-layout';
import styles from './_overview-customizable.module.scss';
import { IPageLayout, clearSavedPageLayout, createPageLayout, updatePageLayout } from '../../../../store/preferences/actions';
import { useDialog } from '../../../../providers/DialogProvider';
import LayoutSelectorDialog from '../../../Dialogs/LayoutSelectorDialog/LayoutSelectorDialog';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { Input } from '@progress/kendo-react-inputs';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import LineStyleOutlinedIcon from '@mui/icons-material/LineStyleOutlined';
import { Label } from "@progress/kendo-react-labels";
import GoogleMap from '../../../GoogleMap/GoogleMap';
import CustomFieldEditor from '../../../CustomFieldEditor/CustomFieldEditor';
import { includes, isNil } from 'ramda';
import { ICustomValuePair } from '../../../../Interfaces/Application/ICustomValuePair';
import { useConfiguration } from '../../../../Data/ConfigurationData';
import IndirectProfitCalculator, { IIndirectProfitCalculatorData } from '../../../IndirectProfitCalculator/IndirectProfitCalculator';
import { DealerMiscFee } from '../../../../Data/IDealertrackDealerDecisionData';
import { ApplicationSummaryCard } from "../../../ApplicationSummaryCard/ApplicationSummaryCard";
import FinanceQuoterContainer from "../../../Quoters/FinanceQuoter/FinanceQuoterContainer";
import LeaseQuoter from "../../../Quoters/LeaseQuoter/LeaseQuoter";
import { BureauFavorite } from '../../../../Data/BureauFavorites';
import {getAppQuoteByType} from "../../../Quoters/IndirectQuoter/IndirectQuoterUtils";
import { IQuote } from '../../../../Interfaces/Common/IQuote';
import {IndirectQuoteType} from "../../../../Data/IndirectQuoteType";
import { useStatusWorkflowUtils } from '../../../../hooks/UseStatusWorkflowUtils';
import {JSX} from "react/jsx-runtime";
import { isIndirectConvertedToFinance } from '../../../../utils/quoteUtils';

export const TileHeader = (headerProps: { title: string, icon: JSX.Element, actions: ReactNode }) => {
  return (
    <div style={{ display: 'flex', flex: 1, flexDirection: 'row' }}>
      <div style={{ display: 'flex', flex: 1, flexDirection: 'row', alignItems: 'center' }}>
        {headerProps.icon}
        <div style={{ marginLeft: '10px' }}>{headerProps.title}</div>
      </div>
      <div style={{ display: 'flex', flex: 1, flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center' }}>
        {headerProps.actions}
      </div>
    </div>
  )
}

export interface OverviewCustomizableProps {
  configuration: IConfiguration;
  navigate: (tabName: TabName) => void;
  setNestedTabNameFlag: (arg0: string | undefined) => void;
  handleSaveAppFromOverview: (currentApp: IApplication, originalApplicationStatus: string, bypassAppRequirements: boolean) => Promise<void>;
}

const getProfitCalculatorData = (app: IApplication, miscFeeInformationList: DealerMiscFee[], vehicleDepreciationRate?: number | null, dealerRequestedQuote?: IQuote): IIndirectProfitCalculatorData => {
  const quote = app.applicationQuotes.length === 3
    ? app.applicationQuotes[2].quote
    : app.applicationQuotes.length === 2
      ? app.applicationQuotes[1].quote
      : app.applicationQuotes.length === 1
        ? app.applicationQuotes[0].quote
        : undefined;

  const payment = quote?.payment ?? 0;
  const tradeInValue = quote?.tradeAllowance ?? 0;


  let miscDealerFees = 0
  let miscLenderFees = 0
  if (dealerRequestedQuote) {
    miscFeeInformationList.forEach((mfi) => {
      if (mfi.feeType === 'dealer') {
        miscDealerFees += dealerRequestedQuote[mfi.key];
      } else if (mfi.feeType === 'lender') {
        miscLenderFees! -= dealerRequestedQuote[mfi.key];
      }
    });
  }

  var dealerFeesCalc = (dealerRequestedQuote?.creditLifeInsurance ?? 0) + miscDealerFees + (dealerRequestedQuote?.taxTitleAndLicense ?? 0) + (dealerRequestedQuote?.docFee ?? 0) +
    (dealerRequestedQuote?.serviceContract ?? 0) + (/*dealerRequestedQuote?.other ??*/ 0) + (dealerRequestedQuote?.disabilityInsurance ?? 0) + (dealerRequestedQuote?.gapInsurance ?? 0) +
    (dealerRequestedQuote?.taxes ?? 0)

  return {
    netTradeAllowance: (dealerRequestedQuote?.tradeAllowance ?? 0) - (dealerRequestedQuote?.tradeLien ?? 0),
    cashDownPayment: quote?.cashDown ?? 0,
    // intoTotalCostPercent: app.vehicle?.carPrice ? `${multiply(divide(add(add(tradeInValue, (quote?.cashDown ?? 0)), miscDealerFees), app.vehicle.carPrice), 100).toFixed(2)}%` : 'N/A',
    intoTotalCostPercent: app.vehicle?.carPrice ? `${multiply(divide((quote?.cashDown ?? 0), add(add(tradeInValue, dealerFeesCalc), app.vehicle.carPrice)), 100).toFixed(2)}%` : 'N/A',
    purchasePrice: app.vehicle?.carPrice ?? 0,
    dealerFees: dealerFeesCalc,
    lenderFees: (quote?.lenderFees ?? 0) + miscLenderFees,
    // lenderWarranty: (dealerDecisionData?.serviceContract ?? 0)  + (dealerDecisionData?.otherInsuranceOrService ?? 0),
    tradeInAllowance: tradeInValue,
    amountToFinance: quote?.amountToFinance ?? 0,
    quote: quote,
    monthlyDepreciationRate: vehicleDepreciationRate,
    bookValue: app.vehicle?.bookoutValue ?? 0
  }
}


export const OverviewCustomizable: React.FC<OverviewCustomizableProps> = props => {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const { data: outstandingAppCustomerStips } = useOutstandingApplicationCustomerStipulations(currentApp ? currentApp.id : Constants.emptyGuid, false);
  const primary = currentApp && getApplicant(currentApp, Constants.roleApplicantId, true);
  const cobuyer1 = currentApp && getApplicant(currentApp, Constants.roleCoApplicant1Id, true);
  const dispatch = useDispatch<ApplicationDispatch>();
  const dispatchNew = useDispatch();
  const activeUser = useSelector((state: AppState) => state.applications.loggedInUser?.employee);
  const organizations = useSelector((state: AppState) => state.applications.lookups.organizations);
  // const dealerDecisionData = useSelector((state: AppState) => state.applications.dealertrackDealerDecisionData);
  const dealerRequestedQuote = getAppQuoteByType(currentApp, IndirectQuoteType.DealerRequested);
  const labelProps: GridProps = { xs: 6, sm: 5, };
  const valueProps: GridProps = { xs: 6, sm: 7, };
  const googleMapsKey = props.configuration?.googleMapsEmbedKey;
  // const origin = currentApp?.location?.address?.trim() && encodeURIComponent(currentApp?.location?.address?.trim());
  const origin = organizations.find(o => o.id.toLowerCase() === currentApp?.organizationId?.toLowerCase())?.mapAddress;
  const primaryCustomerAddress = primary?.customer?.customerAddress?.[0];
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  const quotePayTypes = useSelector((state: AppState) => state.applications.quotePayTypes);
  const businessRulesEngine = useBusinessRulesEngine();
  const employees: IEmployee[] = useSelector((state: AppState) => state.applications.employees);
  const pageLayouts = useSelector((state: AppState) => state.preferences.pageLayouts);
  const useMobileWidth = useMediaQuery('(max-width:550px)');
  const [showMoreQuoteFields, setShowMoreQuoteFields] = useState(!props.configuration?.isFinanceCompany);
  const [inRearrangeMode, setInRearrangeMode] = useState(false);
  const [editingPageLayout, setEditingPageLayout] = useState<IPageLayout | null>(null);
  const savedPageLayout = useSelector((state: AppState) => state.preferences.savedPageLayout);
  const customFields = useSelector((state: AppState) => state.applications.customFields);
  const applicationCustomFields = useSelector((state: AppState) => state.applications.editingApp?.applicationCustomFields);
  const favorites: BureauFavorite[] = useSelector((state: AppState) => state.applications.lookups.bureauFavorites.favorites);
  const { data: configuration } = useConfiguration();
  const [openDialog, closeDialog] = useDialog();
  const vehicleDepreciationRate = useSelector((appState: AppState) => appState.applications.lookups.vehicleDepreciationRate);
  const userCanViewProfitCalculator = activeUser && userHasPermission(activeUser, permissions.viewProfitCalculator, PermissionSet.R);
  const miscFeeInformationList: DealerMiscFee[] = props.configuration?.miscFeeInformationList ?? []
  useEffect(() => {
    setShowMoreQuoteFields(!props.configuration?.isFinanceCompany);
  }, [props.configuration?.isFinanceCompany]);

  const [selectedPageLayout, setSelectedPageLayout] = useState<IPageLayout | null>(null);
  // If pageLayouts is set and has value set to initial item if selected pagelayout is not set
  useEffect(() => {
    if (selectedPageLayout === null && pageLayouts && pageLayouts.length > 0) {
      setSelectedPageLayout(pageLayouts[0]);
    }
  }, [pageLayouts, selectedPageLayout]);

  useEffect(() => {
    if (savedPageLayout) {
      setSelectedPageLayout(savedPageLayout);
      dispatchNew(clearSavedPageLayout());
    }
  }, [savedPageLayout]);
  // useEffect(() => {
  //   console.log('In Here')
  //   setData(gridLayout);
  // }, [useMobileWidth]);

  const gridLayout = useMobileWidth === true
    ? OverviewLayouts.buyHerePayHereMobile
    : OverviewLayouts.buyHerePayHere;

  const handleReposition = (e: TileLayoutRepositionEvent) => {
    const updatedPageLayout: IPageLayout = editingPageLayout ? {
      ...editingPageLayout!,
      positions: e.value.map((v: TileStrictPosition, i: number) => {
        return {
          ...v,
          sortOrder: i
        }
      })
    } : {
      ...selectedPageLayout!,
      positions: e.value.map((v: TileStrictPosition, i: number) => {
        return {
          ...v,
          sortOrder: i
        }
      })
    };

    if (editingPageLayout) {
      setEditingPageLayout(updatedPageLayout);
    } else {
      setSelectedPageLayout(updatedPageLayout);
    }
    // console.log(e.value);
  };
  const [data, setData] = React.useState<Array<any>>(gridLayout);
  const address = !primaryCustomerAddress
    ? null
    : encodeURIComponent(`${primaryCustomerAddress?.street1 || ''} ${primaryCustomerAddress?.street2 || ''} ${primaryCustomerAddress?.city || ''} ${primaryCustomerAddress.state?.abbreviation || ''} ${primaryCustomerAddress?.zipCode || ''} ${primaryCustomerAddress?.country || ''}`?.trim() ?? '');

  const googleMapsIframeSrc =
    !props.configuration?.googleMapsEmbedKey
      ? ''
      : origin
        ? `https://www.google.com/maps/embed/v1/directions?key=${googleMapsKey}&origin=${origin}&destination=${address}`
        : `https://www.google.com/maps/embed/v1/place?key=${googleMapsKey}&q=${address}`;

  const underwriterAppQuote = getAppQuoteByType(currentApp, IndirectQuoteType.Underwriter);

  // const isAnyRequiredChecklistIncomplete = (requiredChecklistIds: string[]): boolean => {
  //   let areThereAnyChecklistsIncomplete = false;
  //   requiredChecklistIds.forEach(requiredChecklistId => {
  //     const matchedChecklist = currentApp?.applicationChecklists?.find(ac => ac.checklistId.toUpperCase() === requiredChecklistId);
  //     if (!hasValue(matchedChecklist?.approversSignature) || !hasValue(matchedChecklist?.approvedDate)) {
  //       areThereAnyChecklistsIncomplete = true;
  //     }
  //   })

  //   return areThereAnyChecklistsIncomplete;
  // };

  // const preventDeskingDueToIncompleteChecklist = currentApp &&
  //   props.configuration?.requiredChecklistsForDesking &&
  //   props.configuration.requiredChecklistsForDesking.length > 0
  //   ? isAnyRequiredChecklistIncomplete(props.configuration.requiredChecklistsForDesking)
  //   : false;

  // const preventDeskingDueToIncompleteStips = props.configuration.featureFlags?.[FeatureFlagIds.DisableDeskingIfRequiredStipsIncomplete] === true
  //   && (isNil(outstandingAppCustomerStips) || outstandingAppCustomerStips.some(oacs => oacs.numberOfDocumentsSubmitted < oacs.numberOfDocumentsRequired));

  //used for testing pre configuration item
  // const checklistIds = ["C06B6440-FAA5-4BFA-AFFA-604BBD99A21D"]
  // const preventDeskingDueToIncompleteChecklist = currentApp
  //   ? isAnyRequiredChecklistIncomplete(checklistIds)
  //   : false;

  //used for testing prefeature flag
  // const preventDeskingDueToIncompleteStips = invalidAppCustomerStipulationGroups.some(sg => sg.invalidStipulationCategories.length > 0);

  // const preventDesking = preventDeskingDueToIncompleteChecklist || preventDeskingDueToIncompleteStips;

  const workflowUtilStatuses = useStatusWorkflowUtils();
  const updateApplication = (fn: (app: IApplication) => IApplication | void) => {
    const newValue = produce(currentApp, fn);
    if (newValue) {
      dispatch(updateEditingApp(newValue, businessRulesEngine));
    }
  };

  if (props.configuration.clientId === 'ATC' && currentApp) {
    return <OverviewATC
      {...props}
      appCustomerStipDocRequirements={outstandingAppCustomerStips ?? []}
      appCustomers={currentApp.appCustomers || []}
      navigate={props.navigate}
      setNestedTabNameFlag={props.setNestedTabNameFlag}
      handleSaveAppFromOverview={props.handleSaveAppFromOverview}
    />;
  }

  // const showQuoter = !!currentApp && currentApp.loanOriginationType === ApplicationLoanOriginationType.Direct
  //   && props.configuration.featureFlags?.[FeatureFlagIds.CanViewQuoterScreen] === true
  //   && !applicationIsBeforeOldQuoterCutoff;

  const isIndirectConvertedToFinanceResult = isIndirectConvertedToFinance(currentApp, props.configuration);

  const showFinanceQuoter = usesFinanceQuote(currentApp?.quoteType) || isIndirectConvertedToFinanceResult;
  const showQuoter = showFinanceQuoter || currentApp?.quoteType === ApplicationQuoteType.Lease;

  const getDecisionBoxForClientAndRole = (): JSX.Element => {
    if (!currentApp) {
      return <div></div>
    }

    // Direct apps
    if (showQuoter) {
      if (showFinanceQuoter) {
        return <FinanceQuoterContainer
          {...props}
          financeLoanClasses={lookups.financeLoanClasses}
          lookups={lookups}
          preventDesking={workflowUtilStatuses.preventDeskingOrPosting ?? true}
        />
      }
      if (currentApp.quoteType === ApplicationQuoteType.Lease && lookups.leaseLoanClasses.length > 0 && quotePayTypes.length > 0) {
        return <LeaseQuoter
          {...props}
          lookups={lookups}
          preventDesking={workflowUtilStatuses.preventDeskingOrPosting ?? true}
        />
      }
    }

    // If they are indirect or an Easton app before the cutover
    // It shows the decision box based on role
    return (
      <div>
        {canUnderwrite(activeUser)
          ?
          <DecisionBoxIndirectUnderwriterView
            updateApplication={updateApplication}
            handleSaveAppFromOverview={props.handleSaveAppFromOverview}
            configuration={props.configuration}
          />
          :
          <DecisionBoxIndirectDealerView
            updateApplication={updateApplication}
            configuration={props.configuration}
          />
        }
        <StatusWorkflowComponent
          style={{ padding: '5px', paddingLeft: '15px' }}
          disableDeskingOrPosting={workflowUtilStatuses.preventDeskingOrPosting ?? true}
        />
      </div>
    );
  }

  const appStatusesPastSubmission = [
    Constants.applicationStatuses.desking,
    Constants.applicationStatuses.underwriting,
    Constants.applicationStatuses.approved,
    Constants.applicationStatuses.denied,
    Constants.applicationStatuses.conditionallyApproved,
    Constants.applicationStatuses.finalizedPosted
  ];

  const showTheIndirectApplicationDecisionInfoCard: boolean =
    !!currentApp
    && (currentApp.quoteType === ApplicationQuoteType.Indirect)
    && appStatusesPastSubmission.includes(currentApp.appStatusId.toUpperCase());


  const handleCustomFieldUpdate = (field: ICustomValuePair): void => {
    if (currentApp) {
      updateApplicationCustomFields([{ formattedValue: field.formattedValue, customFieldName: field.customFieldName, valueType: field.valueType }], currentApp, dispatch, businessRulesEngine);
    }
  };

  const tiles: TileLayoutItem[] = [];
  const hiddenTiles = props.configuration.hiddenOverviewScreenWidgets ?? [];
  if (currentApp) {
    if (hasExistingCustomerInformation(currentApp) && !hiddenTiles.includes('ExistingCustomerInformation')) {
      tiles.push({
        item: <ExistingCustomerInformation appCustomers={currentApp!.appCustomers} />,
        className: inRearrangeMode ? undefined : styles.overflowTile,
        reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        defaultPosition: { order: 0, rowSpan: 1, colSpan: 2, col: 3 }
      });
    } else {
      tiles.push({
        item: <div>Existing Customer Details</div>, defaultPosition: { order: 0, rowSpan: 1, colSpan: 2, col: 3 },
        reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        className: inRearrangeMode ? undefined : styles.hiddenTile,
      })
    }
    tiles.push({
      item: <ApplicationDetailsCard {...props} updateApplication={updateApplication} handleSaveAppFromOverview={props.handleSaveAppFromOverview}/>,
      className: inRearrangeMode ? undefined : styles.overflowTile,
      reorderable: inRearrangeMode,
      resizable: inRearrangeMode,
      defaultPosition: { order: 0, rowSpan: 1, colSpan: 2, col: 3 },
    });
    if (applicationIsPastDesking(currentApp.appStatusId) && currentApp.latestAmsDeskingHistory) {
      tiles.push({
        item: <AmsApplicationInfoCard latestAmsDeskingHistory={currentApp.latestAmsDeskingHistory} employees={employees}  />,
        className: inRearrangeMode ? undefined : styles.overflowTile,
        reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        defaultPosition: { order: 2, rowSpan: 1, colSpan: 1, col: 1 },
      });
    } else {
      tiles.push({
        item: <div>AMS Information</div>, defaultPosition: { order: 2, rowSpan: 1, colSpan: 1, col: 1 }, reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        className: inRearrangeMode ? undefined : styles.hiddenTile,
      })
    }

    if (!hiddenTiles.includes('VehicleInfoCard')) {
      tiles.push({
        item: <VehicleInfoCard navigate={props.navigate}  showVehicleComponent={!showQuoter} />,
        className: inRearrangeMode ? undefined : styles.overflowTile,
        reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        defaultPosition: { order: 3, rowSpan: 1, colSpan: 1, col: 1 }
      });
    } else {
      tiles.push({
        item: <div>Vehicle Info</div>, defaultPosition: { order: 3, rowSpan: 1, colSpan: 1, col: 1 }, reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        className: inRearrangeMode ? undefined : styles.hiddenTile,
      })
    }

    if (!hiddenTiles.includes('CustomerCreditBreakdownCard') && favorites?.length > 0) {
      tiles.push({
        item: <CustomerCreditBreakdownCard {...props} currentApp={currentApp}  favorites={favorites} configuration={configuration} />,
        className: inRearrangeMode ? undefined : styles.overflowTile,
        reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        defaultPosition: { order: 1, rowSpan: 1, colSpan: 1, col: 2 }

      });
    } else {
      tiles.push({
        item: <div>Customer Credit Breakdown</div>, defaultPosition: { order: 1, rowSpan: 1, colSpan: 1, col: 2 }, reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        className: inRearrangeMode ? undefined : styles.hiddenTile,
      })
    }
    if (showTheIndirectApplicationDecisionInfoCard) {
      tiles.push({
        item: <IndirectApplicationDecisionInfoCard underwriterQuote={underwriterAppQuote?.quote} currentApp={currentApp} labelProps={labelProps} valueProps={valueProps}  primaryApplicant={primary} />,
        className: inRearrangeMode ? undefined : styles.overflowTile,
        reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        defaultPosition: { order: 13, rowSpan: 4, colSpan: 3, col: 2 }
      });
    } else {
      tiles.push({
        item: <div>Indirect Application Decision Info</div>, defaultPosition: { order: 13, rowSpan: 4, colSpan: 3, col: 2 }, reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        className: inRearrangeMode ? undefined : styles.hiddenTile,
      })
    }
    tiles.push({
      item: <ApplicationSummaryCard navigate={props.navigate}  currentApp={currentApp}/>,
      className: inRearrangeMode ? undefined : styles.overflowTile,
      reorderable: inRearrangeMode,
      resizable: inRearrangeMode,
      defaultPosition: { order: 7, rowSpan: 4, colSpan: 1, col: 5 }
    });
    tiles.push({
      item: getDecisionBoxForClientAndRole(),
      className: inRearrangeMode ? undefined : styles.overflowTile,
      reorderable: inRearrangeMode,
      resizable: inRearrangeMode,
      defaultPosition: { order: 5, rowSpan: 1, colSpan: 1, col: 5 }
    });

    if (!hiddenTiles.includes('CreditQualityAndRiskMetricsCard')) {
      tiles.push({
        item: <CreditQualityAndRiskMetricsCard {...props} currentApp={currentApp} underwriterQuote={underwriterAppQuote?.quote} dispatch={dispatch} cobuyer1={cobuyer1}  />,
        className: inRearrangeMode ? undefined : styles.overflowTile,
        reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        defaultPosition: { order: 6, rowSpan: 1, colSpan: 1, col: 5 }
      });
    } else {
      tiles.push({
        item: <div>Credit Quality and Risk Metrics</div>, defaultPosition: { order: 6, rowSpan: 1, colSpan: 1, col: 5 }, reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        className: inRearrangeMode ? undefined : styles.hiddenTile,
      })
    }

    if (dealerRequestedQuote && !hiddenTiles.includes('IndirectApplicationHistory')) {
      tiles.push({
        item: <IndirectApplicationHistoryCard currentApp={currentApp} dispatch={dispatch} />,
        className: inRearrangeMode ? undefined : styles.overflowTile,
        reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        defaultPosition: { order: 8, rowSpan: 2, colSpan: 1, col: 5 }
      });
    } else {
      tiles.push({
        item: <>Application History</>,
        defaultPosition: { order: 8, rowSpan: 2, colSpan: 1, col: 5 }, reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        className: inRearrangeMode ? undefined : styles.hiddenTile,
      })
    }
    if (props.configuration.featureFlags[FeatureFlagIds.DisplayNotesOnOverview]) {
      tiles.push({
        item: <NotesCard navigate={props.navigate} />,
        className: inRearrangeMode ? undefined : styles.overflowTile,
        reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        defaultPosition: { order: 9, rowSpan: 1, colSpan: 1, col: 5 },
      });
    } else {
      tiles.push({
        item: <div>Notes</div>, defaultPosition: { order: 9, rowSpan: 1, colSpan: 1, col: 5 }, reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        className: inRearrangeMode ? undefined : styles.hiddenTile,
      })
    }
    if (props.configuration.featureFlags[FeatureFlagIds.UseLOSChat]) {
      tiles.push({
        item: <Card>
          <CardHeader
            avatar={<ChatIcon fontSize="small" />}
            title="Chat"
          />
          <ApplicationChatComponent navigate={props.navigate} currentApp={currentApp} organizations={organizations} configuration={props.configuration} />
        </Card>,
        className: inRearrangeMode ? undefined : styles.overflowTile,
        reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        defaultPosition: { order: 10, rowSpan: 1, colSpan: 1, col: 5 }
      });
    } else {
      tiles.push({
        item: <span>Chat</span>, defaultPosition: { order: 10, rowSpan: 1, colSpan: 1, col: 5 }, reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        className: inRearrangeMode ? undefined : styles.hiddenTile,
      })
    }

    if (!hiddenTiles.includes('GoogleMap')) {
      tiles.push({
        item: <GoogleMap googleMapsIframeSrc={googleMapsIframeSrc} navigate={props.navigate} />,
        className: inRearrangeMode ? undefined : styles.overflowTile,
        reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        defaultPosition: { order: 12, rowSpan: 2, colSpan: 1, col: 5 }
      });
    } else {
      tiles.push({
        item: <div>Google Map</div>, defaultPosition: { order: 12, rowSpan: 2, colSpan: 1, col: 5 }, reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        className: inRearrangeMode ? undefined : styles.hiddenTile,
      })
    }
    if (!hiddenTiles.includes('CustomFieldEditor')) {
      tiles.push({
        item: <CustomFieldEditor
          applicaitonCustomFields={applicationCustomFields?.filter(acf => includes(acf.customFieldId, customFields.filter(cf => cf.displayInCustomFieldEditorWidget === true).map(cf => cf.id))) ?? []}
          navigate={props.navigate}
          onUpdateApplicationCustomField={handleCustomFieldUpdate}
        />,
        className: inRearrangeMode ? undefined : styles.overflowTile,
        reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        defaultPosition: { order: 16, rowSpan: 1, colSpan: 1, col: 1 }
      });
    } else {
      tiles.push({
        item: <div>Custom Fields</div>, defaultPosition: { order: 17, rowSpan: 1, colSpan: 1, col: 1 }, reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        className: inRearrangeMode ? undefined : styles.hiddenTile,
      });
    }
    if (props.configuration.featureFlags[FeatureFlagIds.ShowMissingStipulations]) {
      tiles.push({
        item: <InvalidStipulationsCard appCustomerStipDocRequirements={outstandingAppCustomerStips ?? []} appCustomers={currentApp.appCustomers || []} navigate={props.navigate} />,
        className: inRearrangeMode ? undefined : styles.overflowTile,
        reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        // defaultPosition: { order: 16, rowSpan: 2, colSpan: 2, col: 1 }
      });
    } else {
      tiles.push({
        item: <div>Invalid Stips</div>,
        // defaultPosition: { order: 16, rowSpan: 2, colSpan: 2, col: 1},
        reorderable: true,
        resizable: true,
        className: inRearrangeMode ? undefined : styles.hiddenTile,
      });
    }

    if ((currentApp?.quoteType === ApplicationQuoteType.Indirect) && userCanViewProfitCalculator) {
      tiles.push({
        item: <IndirectProfitCalculator
          profitCalculatorData={getProfitCalculatorData(currentApp, miscFeeInformationList, vehicleDepreciationRate, dealerRequestedQuote?.quote)}
        />,
        className: inRearrangeMode ? undefined : styles.overflowTile,
        reorderable: inRearrangeMode,
        resizable: inRearrangeMode,
        // defaultPosition: { order: 17, rowSpan: 2, colSpan: 2, col: 1 }
      });
    } else {
      tiles.push({
        item: <div>Profit Calculator</div>,
        // defaultPosition: { order: 16, rowSpan: 2, colSpan: 2, col: 1},
        reorderable: true,
        resizable: true,
        className: inRearrangeMode ? undefined : styles.hiddenTile,
      })
    }

  }

  const openLayoutSelectorDialog = () => {
    // console.log('open layout selector dialog');
    openDialog({
      fullScreen: false,
      children: <LayoutSelectorDialog
        pageLayouts={pageLayouts}
        selectedLayout={selectedPageLayout}
        accept={(result: IPageLayout) => {
          setSelectedPageLayout(result);
          closeDialog();
        }}
        closeDialog={closeDialog}
      />
    })

  }

  return (
    <div className={styles.overviewContainer}>
      <div className={styles.layoutSelector}>
        {configuration?.isSuperUser === true && !inRearrangeMode && (
          <>
            <IconButton
              onClick={() => openLayoutSelectorDialog()}
            >
              <LineStyleOutlinedIcon />
            </IconButton>
            <IconButton
              disabled={!selectedPageLayout}
              onClick={
                () => {
                  setEditingPageLayout({
                    ...selectedPageLayout!,
                    positions: selectedPageLayout!.positions.map((p: TileStrictPosition, index: number) => {
                      return {
                        ...p,
                        sortOrder: index
                      }
                    })
                  });
                  setInRearrangeMode(true);
                }}
            >
              <EditNoteIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="Add Layout"
              onClick={
                () => {
                  setEditingPageLayout({
                    pageName: 'Overview',
                    positions: selectedPageLayout!.positions.map((p: TileStrictPosition, index: number) => {
                      return {
                        ...p,
                        sortOrder: index
                      }
                    }),
                    name: ''
                  });
                  setInRearrangeMode(true);
                }}
            >
              <PostAddOutlinedIcon fontSize="small" />
            </IconButton>
          </>
        )}
        {inRearrangeMode && (
          <>
            <Label style={{ marginLeft: '20px' }}>Name:</Label>
            <Input
              maxLength={100}
              // label={sendToAssignedTo ? "Will send to contact owner." : "Send to the following emails (comma or semi-colon separated)"}
              placeholder='Layout Name'
              validationMessage='Name is required'
              value={editingPageLayout?.name}
              valid={hasValue(editingPageLayout?.name)}
              style={{ width: '200px', marginLeft: '10px', marginRight: '10px' }}
              onChange={(event: any) => {
                const updatedPageLayout: IPageLayout = {
                  ...editingPageLayout!,
                  name: event.value as string
                };
                setEditingPageLayout(updatedPageLayout);
              }}
            />
            <Label>Description:</Label>
            <Input
              maxLength={500}
              // label={sendToAssignedTo ? "Will send to contact owner." : "Send to the following emails (comma or semi-colon separated)"}
              placeholder='Layout Name'
              validationMessage='Name is required'
              value={editingPageLayout?.description}
              valid={hasValue(editingPageLayout?.description)}
              style={{ width: '200px', marginLeft: '10px', marginRight: '10px' }}
              onChange={(event: any) => {
                const updatedPageLayout: IPageLayout = {
                  ...editingPageLayout!,
                  description: event.value as string
                };
                setEditingPageLayout(updatedPageLayout);
              }}
            />
            <IconButton
              disabled={!hasValue(editingPageLayout?.name)}
              onClick={() => {
                if (editingPageLayout?.id) {
                  dispatchNew(updatePageLayout(editingPageLayout!));
                } else {
                  dispatchNew(createPageLayout(editingPageLayout!));
                }
                setInRearrangeMode(false);
                setSelectedPageLayout(editingPageLayout);
                setEditingPageLayout(null);
              }}
            >
              <SaveIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => {
                setInRearrangeMode(false);
                setEditingPageLayout(null);
              }}
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </div>
      <TileLayout
        columns={12}
        rowHeight={100}
        positions={editingPageLayout?.positions ?? selectedPageLayout?.positions ?? undefined}  // TODO: Get Default Positions instead of undefined, then undefined if no default positions which should never happen
        gap={{ rows: 10, columns: 10 }}
        items={tiles}
        onReposition={handleReposition}
        ignoreDrag={(e) => { return e.target.nodeName == "INPUT"; }}
      />
    </div>
  );
}
